import { Injectable, inject } from '@angular/core';

import { AnalyticsData, Anonymizers, PageviewAnalyticsData, AuthenticationStatus } from './analytics.model';
import { ApAnonymizerService } from './datalayer/anonymizer.service';
import { ApDatalayerService } from './datalayer/datalayer.service';
import { ApPageviewDataService } from './pageview-data/pageview-data.service';

/**
 * @ngdoc service
 * @name ApAnalyticsService
 * @module ApAnalyticsModule
 * @description The ApAnalyticsService allows applications to push analytics events.
 *
 * @seeAlso There is a Chrome plugin that can listen to the analytics event bus (DEQ),
 * this plugin helps visualising analytics events:
 * https://chrome.google.com/webstore/detail/adversitement-dcfdeqfacto/keeieokkdbgonooajncmfpckmjeclbpc
 */
@Injectable({ providedIn: 'root' })
export class ApAnalyticsService {
    private readonly datalayerService = inject(ApDatalayerService);
    private readonly pageviewDataService = inject(ApPageviewDataService);

    public static readonly anonymizers: Anonymizers = {
        amount: ApAnonymizerService.anonymizeAmount,
        date: ApAnonymizerService.anonymizeDate,
        birthDate: ApAnonymizerService.anonymizeBirthDate,
        zipCode: ApAnonymizerService.anonymizeZipCode,
        percentage: ApAnonymizerService.anonymizePercentage,
        strip: () => undefined
    };

    /**
     * @ngdoc method
     * @methodOf ApAnalyticsService
     * @name ApAnalyticsService#push
     * @description
     * Push a "dimension"'s data to {@link fc-datalayer}.
     *
     * @param name
     * The name of the "dimension" to push to the datalayer. Valid names include:
     * * `pageview`
     * * `error`
     * * `form step`
     * * `video action`
     * * `document action`
     * * `interface change`
     * See the top level properties of
     * [`dataSchema`](https://bitbucket.verz.local:8443/projects/FC/repos/fc-datalayer/browse/src/dataSchema.js)
     * in fc-datalayer.
     *
     * @param data
     * The object containing the data for the "dimension". See the `properties` of each dimension in
     * [fc-datalayer](https://bitbucket.verz.local:8443/projects/FC/repos/fc-datalayer/browse/src/dataSchema.js).
     *
     * Some properties are set automatically by fc-analytics, some need to be set for the entire app
     * (see {@link fcAnalyticsServiceProvider}) and the rest need to be passed with this param.
     * Some properties are required and most have a certain type. fc-datalayer will warn when
     * invalid "dimension" data is pushed to it.
     * *Any data property that's already set by {@link fcDefaultData} or
     * {@link fcAnalyticsServiceProvider#setAppData} can be overwritten by adding those properties
     * here*.
     *
     * When `name` is `'form step'`, `data` requires a `formFieldValues` array.
     * Some form fields *must* be anonymized, these are: amounts, dates, birth dates and zip codes.
     * Anonymization happens in fc-datalayer, but it doesn't know which fields need to be anonymized.
     * To signal a field for anonymization, add an `anonymizeAs` property to the item in `formFieldValues`.
     *
     * For example:
     *
     * ```js
     * ApAnalyticsService.push('form step', {
     *         analyticsEvents: 'some event',
     *         formName: 'some form',
     *         formType: 'some type',
     *         formStepName: 'some step',
     *         formStepNumber: 2,
     *         formFieldValues: [
     *             {
     *                 name: 'someAmount',
     *                 value: ctrl.someAmount,
     *                 anonymizeAs: 'amount'
     *             },
     *             {
     *                 name: 'someDate',
     *                 value: ctrl.someDate,
     *                 anonymizeAs: 'date'
     *             },
     *             {
     *                 name: 'someBirthDate',
     *                 value: ctrl.someBirthDate,
     *                 anonymizeAs: 'birthDate'
     *             },
     *             {
     *                 name: 'someZipCode',
     *                 value: ctrl.someZipCode,
     *                 anonymizeAs: 'zipCode'
     *             }
     *         ]
     * });
     * ```
     */
    public push(data: AnalyticsData): void {
        this.datalayerService.pushData(data.event, data);
    }

    /**
     * @ngdoc method
     * @methodOf ApAnalyticsService
     * @name ApAnalyticsService#pushPageviewOnRouteChangeSuccess
     * @description
     * Calls fcAnalyticsService#push on each $routeChangeSuccess event that
     * has also loaded a template. This ignores route changes that are triggered by
     * redirects (e.g. $routeProvider's `otherwise` method).
     * *Does not work when using ui-router.*
     *
     * This method is supposed to be used in the run block of the app.
     */
    public pushPageview(
        pageType: string,
        productName: string,
        productCategory: string,
        pageSiteSegment: string,
        pageSiteSection: string,
        platformName: string,
        analyticsEvents?: string[],
        caseId?: string,
        authenticationStatus: AuthenticationStatus = AuthenticationStatus.AUTHENTICATED,
        pageName?: string
    ): void {
        const pageviewData: PageviewAnalyticsData = this.pageviewDataService.getPageviewData(
            pageType,
            productName,
            productCategory,
            pageSiteSegment,
            pageSiteSection,
            platformName,
            analyticsEvents,
            caseId,
            authenticationStatus,
            pageName
        );
        this.push(pageviewData);
    }
}
